/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import urlJoin from 'proper-url-join';
import React from 'react';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { InstanceState } from '../../../../ts/instance';
import { TrialData } from '../../../../ts/plan';
import { AppDispatch, RootState } from '../../../../ts/state';
import * as instanceActions from '../../store/instance/actions';
import {
  selectPlusTrialData,
  isUpdatingInstance
} from '../../store/instance/selectors';
import { PLAN_NAMES } from '../../store/plan/constants';
import { selectInstanceState } from '../../store/session/selectors';
import history from '../../util/history';
import TrialManager, { TrialActions } from '../../util/plusTrialManager';
import * as segment from '../../util/trackers/track-when-ready';
import { getPath } from '../../util/url';

const PAGE_PATHS = {
  planLimit: '/:instance/planLimit'
};

export interface PlanLimitCheckProps {
  region: string;
  instance: string;
  instanceState: InstanceState;
  trialData: TrialData;
  trialActions: TrialActions;
  trialUpdating: boolean;
}
export class PlanLimitCheck extends React.Component<PlanLimitCheckProps> {
  componentDidUpdate() {
    const { region, instance, instanceState } = this.props;
    if (region && instance && instanceState) {
      const plan = instanceState.plan;
      if (plan === PLAN_NAMES.LITE || plan === PLAN_NAMES.PLUS_TRIAL) {
        if (this.checkApiLimit()) {
          return this.reroute(
            'planLimit',
            `limit=api&plan=${instanceState.plan}`
          );
        }
      }
      if (plan === PLAN_NAMES.PLUS_TRIAL) {
        if (this.checkTrialLimit()) {
          return this.reroute(
            'planLimit',
            `limit=trial&plan=${instanceState.plan}`
          );
        }
      }
    }
  }

  checkApiLimit() {
    const { instanceState } = this.props;
    return instanceState.state === 'inactive';
  }

  checkTrialLimit() {
    const { instance, trialData, trialActions, trialUpdating } = this.props;
    const trialMgr = new TrialManager(instance, trialData, trialActions);
    if (trialMgr.trialSupported()) {
      if (!trialMgr.trialStarted() && !trialUpdating) {
        (trialMgr.startTrial() as Promise<any>).then(() =>
          segment.customEvent(
            'Trial Started',
            trialData.start_date,
            'start date',
            true,
            null
          )
        );
      } else {
        return trialMgr.daysRemaining() === 0;
      }
    }
    return false;
  }

  reroute(page = 'planLimit', params) {
    const { instance } = this.props;
    const pagePath = PAGE_PATHS[page];
    if (!matchPath(history.location.pathname, pagePath)) {
      const path = urlJoin(
        getPath(document.baseURI),
        encodeURIComponent(instance),
        page
      );
      history.push(`${path}?${params}`);
    }
  }

  // This component has no UI.
  render() {
    return null;
  }
}

const mapStateToProps = (state: RootState) => ({
  region: state.session.region,
  instance: state.session.instance,
  instanceState: selectInstanceState(state) || {},
  trialData: selectPlusTrialData(state),
  trialUpdating: isUpdatingInstance(state)
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  trialActions: bindActionCreators(instanceActions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(PlanLimitCheck);
